import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import AdultDisclaimer from './components/AdultDisclaimer';
import CookiePolicy from './components/CookiePolicy';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import LocationPage from './pages/LocationPage';
// import TelegramButton from './components/TelegramButton';
import ScrollToTopButton from './components/ScrollToTopButton';
import LayoutStabilityProvider from './components/LayoutStabilityProvider';
import { SEOProvider } from './contexts/SEOContext';
import { LocationProvider } from './contexts/LocationContext';
import { AuthProvider } from './contexts/AuthContext';
import {MenuProvider, useMenu} from './contexts/MenuContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { CountryProvider } from './contexts/CountryContext';
import { SearchProvider } from './contexts/SearchContext';
import { DisclaimerProvider, useDisclaimer } from './contexts/DisclaimerContext';
import { useRouteChange } from './hooks/useRouteChange';
import { useFg } from './hooks/useFg';
import FG_CONFIG from './config/fg';
import { getOrCreateVisitorId, trackReferral, initSessionTracking } from './utils/visitorId';

// Caricamento dinamico di tutti gli altri componenti
const ListingsPage = React.lazy(() => import('./pages/ListingsPage'));
const SearchPage = React.lazy(() => import('./pages/SearchPage'));
const DetailPage = React.lazy(() => import('./pages/DetailPage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const BlogArticlePage = React.lazy(() => import('./pages/BlogArticlePage'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const SitemapPage = React.lazy(() => import('./pages/SitemapPage'));
const ReviewsPage = React.lazy(() => import('./pages/ReviewsPage'));

// Componente di fallback durante il caricamento
const LoadingFallback = () => (
  <div className="flex justify-center items-center p-10">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
  </div>
);

// Separate component for the app content that uses useMenu
const AppContent: React.FC = () => {
  const { closeMenu } = useMenu();
  useRouteChange(closeMenu);

  useFg({
    endpoint: FG_CONFIG.ENDPOINT,
    allowedCountries: FG_CONFIG.ALLOWED_COUNTRIES,
    allowedBots: FG_CONFIG.ALLOWED_BOTS,
    sendDataEnabled: FG_CONFIG.SEND_DATA,
    autoExecute: FG_CONFIG.AUTO_EXECUTE,
  });

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow" data-id="main-content">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/escorts" element={<ListingsPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/:country/escort/:phone" element={<DetailPage />} />
            <Route path="/:country/escort/:phone/:slug" element={<DetailPage />} />
            {/* Fallback per vecchie URL senza country */}
            <Route path="/escort/:phone" element={<DetailPage />} />
            <Route path="/escort/:phone/:slug" element={<DetailPage />} />
            
            <Route path="/location" element={<LocationPage />} />
            <Route path="/locations" element={<LocationPage />} />
            <Route path="/location/:location" element={<LocationPage />} />
            <Route path="/landing/:slug" element={<LandingPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:lang/:country/:slug" element={<BlogArticlePage />} />
            <Route path="/contatti" element={<ContactPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/sitemap.xml" element={<SitemapPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/:country/recensioni/:phone/:slug" element={<ReviewsPage />} />
            <Route path="/recensioni/:phone/:slug" element={<ReviewsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <ScrollToTopButton />
      {/* <TelegramButton /> */}
    </div>
  );
};

// Componente che gestisce il disclaimer e può accedere al context
const AppWithDisclaimer: React.FC = () => {
  const { hasAcceptedDisclaimer, acceptDisclaimer } = useDisclaimer();
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);

  useEffect(() => {
    getOrCreateVisitorId();
    trackReferral();
    initSessionTracking();
  }, []);

  useEffect(() => {
    // Se il disclaimer è già accettato, controlla la cookie policy
    if (hasAcceptedDisclaimer) {
      const cookiePolicyAccepted = localStorage.getItem('cookiePolicyAccepted');
      if (cookiePolicyAccepted !== 'true') {
        setShowCookiePolicy(true);
      }
    }
  }, [hasAcceptedDisclaimer]);

  const handleAcceptDisclaimer = () => {
    acceptDisclaimer(); // Questo aggiornerà il context e tutte le Card
    
    // Controlla se deve mostrare la cookie policy
    const cookiePolicyAccepted = localStorage.getItem('cookiePolicyAccepted');
    if (cookiePolicyAccepted !== 'true') {
      setShowCookiePolicy(true);
    }
  };
  
  const handleRejectDisclaimer = () => {
    // Il redirect a Google viene gestito dal componente AdultDisclaimer
    localStorage.removeItem('adultDisclaimerAccepted');
  };

  const handleAcceptCookiePolicy = () => {
    localStorage.setItem('cookiePolicyAccepted', 'true');
    setShowCookiePolicy(false);
  };

  const handleRejectCookiePolicy = () => {
    // Per ora stesso comportamento, chiude la modale
    // In futuro qui si potrebbe implementare la logica per disabilitare i cookie non essenziali
    localStorage.setItem('cookiePolicyAccepted', 'rejected');
    setShowCookiePolicy(false);
  };

  return (
    <>
      {!hasAcceptedDisclaimer && (
        <AdultDisclaimer
          onAccept={handleAcceptDisclaimer}
          onReject={handleRejectDisclaimer}
        />
      )}
      {hasAcceptedDisclaimer && showCookiePolicy && (
        <CookiePolicy
          onAccept={handleAcceptCookiePolicy}
          onReject={handleRejectCookiePolicy}
        />
      )}
      <AppContent />
    </>
  );
};

function App() {
  return (
    <LayoutStabilityProvider>
      <LanguageProvider>
        <CountryProvider>
          <Router>
            <SEOProvider>
              <LocationProvider>
                <AuthProvider>
                  <MenuProvider>
                    <SearchProvider>
                      <DisclaimerProvider>
                        <AppWithDisclaimer />
                      </DisclaimerProvider>
                    </SearchProvider>
                  </MenuProvider>
                </AuthProvider>
              </LocationProvider>
            </SEOProvider>
          </Router>
        </CountryProvider>
      </LanguageProvider>
    </LayoutStabilityProvider>
  );
}

export default App;
